import {
  type AbstractMesh,
  type CubeTexture,
  EngineStore,
  type ISceneLoaderPlugin,
  type ISceneLoaderPluginAsync,
  type ITextureCreationOptions,
  type Material,
  PBRMaterial,
  Scene,
  SceneLoader,
  StandardMaterial,
  Texture,
} from '@babylonjs/core';
import { GLTFFileLoader, type IGLTFLoaderData } from '@babylonjs/loaders/glTF';
import { BabylonjsModule } from '@modules/babylonjs';
import { Injectable, Zed } from '@modules/core';
import { World } from 'miniplex';

import { PerformanceProfiler, RenderManager } from '../../manager';
import { buildSrcUrl, Profile } from '../../util';
import { EnvironmentLoader } from '../EnvironmentLoader';

import type { IGLTF, IImage, IMaterial, IMesh, ISampler, ITexture, ITextureInfo } from '@babylonjs/loaders/glTF/2.0';

export enum AsyncTextureType {
  BaseColor,
  Metalic,
  Normal,
  Occlusion,
  LightMap,
  Emissive,
}
enum TextureMinFilter {
  NEAREST = 9728,
  LINEAR = 9729,
  NEAREST_MIPMAP_NEAREST = 9984,
  LINEAR_MIPMAP_NEAREST = 9985,
  NEAREST_MIPMAP_LINEAR = 9986,
  LINEAR_MIPMAP_LINEAR = 9987,
}
enum TextureMagFilter {
  NEAREST = 9728,
  LINEAR = 9729,
}
enum TextureWrapMode {
  CLAMP_TO_EDGE = 33071,
  MIRRORED_REPEAT = 33648,
  REPEAT = 10497,
}

type AsyncObjectInfo = {
  meshInfos: AsyncMeshInfo[];
};
type AsyncMeshInfo = {
  meshName: string;
  reflectionProbe?: any;
  mesh?: AbstractMesh;
  material?: Material;
  textureInfos: AsyncTextureInfo[];
};
type AsyncTextureInfo = {
  type: AsyncTextureType;
  textureInfo: ITextureInfo;
  image: IImage;
  sampler: ISampler;
  uri: string;
};

export class AsyncTextureLoader implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('AsyncTextureLoader');

  private readonly ZED_EXTENSION = 'ZED_EXTENSION';

  private readonly scene;
  private readonly options;
  private readonly renderManager;
  private readonly environmentLoader;
  private readonly performanceProfiler;

  private asyncObjectInfo!: AsyncObjectInfo[];
  private loadedTexture!: Map<string, Texture>;
  private loadedEnvironment!: Map<string, CubeTexture>;
  private textureKeys!: Map<string, number>;
  private textureIndex!: Map<string, number>;

  private onPluginActivatedEvent!: (loader: ISceneLoaderPlugin | ISceneLoaderPluginAsync) => void;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [EnvironmentLoader.INJECTABLE_TOKEN]: environmentLoader,
    [PerformanceProfiler.INJECTABLE_TOKEN]: performanceProfiler,
  }: {
    [Zed.WORLD_TOKEN]: World;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [EnvironmentLoader.INJECTABLE_TOKEN]: EnvironmentLoader;
    [PerformanceProfiler.INJECTABLE_TOKEN]: PerformanceProfiler;
  }) {
    this.scene = babylonjs.scene;
    this.options = babylonjs.options;
    this.renderManager = renderManager;
    this.environmentLoader = environmentLoader;
    this.performanceProfiler = performanceProfiler;

    this.initialize();
  }

  private initialize() {
    this.asyncObjectInfo = [];
    this.loadedTexture = new Map();
    this.loadedEnvironment = new Map();
    this.textureKeys = new Map();
    this.textureIndex = new Map();

    this.onPluginActivatedEvent = this.onPluginActivated.bind(this);
    SceneLoader.OnPluginActivatedObservable.removeCallback(this.onPluginActivatedEvent);
    SceneLoader.OnPluginActivatedObservable.add(this.onPluginActivatedEvent);
  }
  public dispose() {
    this.asyncObjectInfo = [];
    this.loadedTexture.clear();
    this.loadedEnvironment.clear();
    this.textureKeys.clear();
    this.textureIndex.clear();
    SceneLoader.OnPluginActivatedObservable.removeCallback(this.onPluginActivatedEvent);
  }

  private _enable: boolean = true;
  public setEnable(enable: boolean) {
    if (this._enable === enable) return;
    this._enable = enable;

    if (enable) {
      this.initialize();
    } else {
      this.dispose();
    }
  }
  private isZEDGltf(data: IGLTFLoaderData) {
    // @ts-ignore
    const extensionsUsed: string[] = data.json['extensionsUsed'];
    return extensionsUsed?.includes(this.ZED_EXTENSION);
  }

  private onPluginActivated(loader: ISceneLoaderPlugin | ISceneLoaderPluginAsync) {
    const isGLTFLoader = loader instanceof GLTFFileLoader;
    if (isGLTFLoader) {
      const gltfLoader = loader as unknown as GLTFFileLoader;

      let enable = true;
      const objectInfos: AsyncMeshInfo[] = [];
      let loaderData: IGLTFLoaderData | undefined;

      gltfLoader.onParsedObservable.add((data) => {
        enable = this.isZEDGltf(data);
        if (enable) {
          loaderData = data;
          this.changeImageURL(loaderData);
          objectInfos.push(...this.getAsyncObjectInfos(loaderData));
          this.clearMaterial(loaderData);
        }
      });

      gltfLoader.onMeshLoadedObservable.add((mesh) => {
        if (enable) {
          if (mesh.material) {
            let index = 0;
            if (!this.textureIndex.has(mesh.name)) {
              this.textureIndex.set(mesh.name, 0);
            } else {
              index = this.textureIndex.get(mesh.name)! + 1;
              this.textureIndex.set(mesh.name, index);
            }

            const info = objectInfos.find((i) => i.meshName === `${mesh.name}_${index}`);
            if (info) {
              info.mesh = mesh;
              info.material = mesh.material;
              mesh.material.onDisposeObservable.add(() => {
                info.material = undefined;
              });
            }
          }
        }
      });

      gltfLoader.onCompleteObservable.add(() => {
        if (enable) {
          this.asyncObjectInfo.push({
            meshInfos: objectInfos,
          });
        }
      });
    }
  }

  private changeImageURL(data: IGLTFLoaderData) {
    // @ts-ignore
    const images: IImage[] = data.json['images'];

    for (const image of images) {
      if (image.uri) {
        image.uri = `${this.options.qualitySettings.textureOption.textureMaxSize}/${image.uri}`;
      }
    }
  }

  private getAsyncObjectInfos(data: IGLTFLoaderData) {
    const gltfData = data.json as IGLTF;

    const meshes: IMesh[] = gltfData.meshes ?? [];
    const images: IImage[] = gltfData.images ?? [];
    const materials: IMaterial[] = gltfData.materials ?? [];
    const samplers: ISampler[] = gltfData.samplers ?? [];
    const textures: ITexture[] = gltfData.textures ?? [];

    return meshes.flatMap((mesh) => {
      const primitiveCount = mesh.primitives.length;

      let i = 0;
      return mesh.primitives.flatMap((primitive) => {
        const material = materials[primitive.material ?? 0];
        const textureInfos = this.getTextureInfos(material);
        const reflectionProbe = material.extras?.reflectionProbe;

        const asyncTextureInfos = this.convertAsyncTextureInfo(textureInfos, textures, images, samplers);
        const meshName = primitiveCount > 1 ? `${mesh.name}_primitive${i++}` : mesh.name!;

        let index = 0;
        if (!this.textureKeys.has(meshName)) {
          this.textureKeys.set(meshName, 0);
        } else {
          index = this.textureKeys.get(meshName)! + 1;
          this.textureKeys.set(meshName, index);
        }

        const key = `${meshName}_${index}`;

        return {
          meshName: key,
          textureInfos: asyncTextureInfos,
          reflectionProbe: reflectionProbe,
        } as AsyncMeshInfo;
      });
    });
  }
  private getTextureInfos(material: IMaterial) {
    return [
      this.options.qualitySettings.textureOption.useBaseColorTexture
        ? this.getTextureInfo(AsyncTextureType.BaseColor, material.pbrMetallicRoughness?.baseColorTexture)
        : undefined,
      this.options.qualitySettings.textureOption.useMetallicRoughnessTexture
        ? this.getTextureInfo(AsyncTextureType.Metalic, material.pbrMetallicRoughness?.metallicRoughnessTexture)
        : undefined,
      this.options.qualitySettings.textureOption.useNormalTexture
        ? this.getTextureInfo(AsyncTextureType.Normal, material.normalTexture)
        : undefined,
      this.options.qualitySettings.textureOption.useAmbientOcclusionTexture
        ? this.getTextureInfo(AsyncTextureType.Occlusion, material.occlusionTexture)
        : undefined,
      this.options.qualitySettings.textureOption.useLightmapTexture
        ? this.getTextureInfo(AsyncTextureType.LightMap, material.extras?.lightmapTexture)
        : undefined,
      this.options.qualitySettings.textureOption.useEmissiveTexture
        ? this.getTextureInfo(AsyncTextureType.Emissive, material.emissiveTexture)
        : undefined,
    ].flatMap((info) => {
      if (info) return info;
      return [];
    });
  }
  private getTextureInfo(type: AsyncTextureType, textureInfo: ITextureInfo | undefined) {
    if (textureInfo) {
      return {
        type: type,
        info: textureInfo,
      };
    }
    return undefined;
  }
  private imagePath?: string;
  public setRootImagePath(path: string) {
    this.imagePath = path;
  }
  private convertAsyncTextureInfo(
    textureInfos: ReturnType<typeof this.getTextureInfos>,
    textures: ITexture[],
    images: IImage[],
    samplers: ISampler[]
  ) {
    const defaultSampler: ISampler = {
      index: -1,
    };
    return textureInfos.flatMap((i) => {
      const textureInfoIndex = i.info.index;
      const samplerIndex = textures[textureInfoIndex].sampler;
      const info: Readonly<AsyncTextureInfo> = {
        type: i.type,
        textureInfo: i.info,
        image: images[textureInfoIndex],
        sampler: samplerIndex ? samplers[samplerIndex] : defaultSampler,
        uri: `${
          this.imagePath ? `${this.imagePath}model/${images[textureInfoIndex].uri}` : images[textureInfoIndex].uri
        }`,
      };
      return info;
    });
  }
  private clearMaterial(data: IGLTFLoaderData) {
    // @ts-ignore
    const materials: IMaterial[] = data.json['materials'];

    for (const material of materials) {
      if (material.pbrMetallicRoughness) {
        material.pbrMetallicRoughness.baseColorTexture = undefined;
        material.pbrMetallicRoughness.metallicRoughnessTexture = undefined;
      }
      if (material.extras) {
        if (material.extras.lightmapTexture) {
          material.extras.lightmapTexture = undefined;
        }
        if (material.extras.reflectionProbe) {
          material.extras.reflectionProbe = undefined;
        }
      }
      material.normalTexture = undefined;
      material.occlusionTexture = undefined;
      material.emissiveTexture = undefined;
    }
  }

  //------------------------------------------------------------------------------------
  // #### Texture Load & Apply ####
  //------------------------------------------------------------------------------------
  public async LoadAndApply() {
    if (!this._enable) return;
    
    const scene = EngineStore.LastCreatedScene;
    if (!scene) return;
    scene.blockMaterialDirtyMechanism = false;

    Profile.start('Total');
    const performance = this.performanceProfiler;

    if (this.options.qualitySettings.textureOption.useLightmapTexture) {
      const key = AsyncTextureType[AsyncTextureType.LightMap];
      Profile.start(key);

      await this.loadTextureByType([AsyncTextureType.LightMap]);
      await this.applyTextureByType([AsyncTextureType.LightMap]);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    if (this.options.qualitySettings.textureOption.useAmbientOcclusionTexture) {
      const key = AsyncTextureType[AsyncTextureType.Occlusion];
      Profile.start(key);

      await this.loadTextureByType([AsyncTextureType.Occlusion]);
      await this.applyTextureByType([AsyncTextureType.Occlusion]);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    if (this.options.qualitySettings.textureOption.useReflectionProbe) {
      const key = 'Refelection Probe';
      Profile.start(key);

      await this.applyReflectionProbe(scene);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    if (this.options.qualitySettings.textureOption.useBaseColorTexture) {
      const key = AsyncTextureType[AsyncTextureType.BaseColor];
      Profile.start(key);

      await this.loadTextureByType([AsyncTextureType.BaseColor]);
      await this.applyTextureByType([AsyncTextureType.BaseColor]);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    if (this.options.qualitySettings.textureOption.useNormalTexture) {
      const key = AsyncTextureType[AsyncTextureType.Normal];
      Profile.start(key);

      await this.loadTextureByType([AsyncTextureType.Normal]);
      await this.applyTextureByType([AsyncTextureType.Normal]);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    if (this.options.qualitySettings.textureOption.useMetallicRoughnessTexture) {
      const key = AsyncTextureType[AsyncTextureType.Metalic];
      Profile.start(key);

      await this.loadTextureByType([AsyncTextureType.Metalic]);
      await this.applyTextureByType([AsyncTextureType.Metalic]);

      Profile.end(key);
      performance.addCustom('Texture', key, Profile.get(key));
    }

    scene.blockMaterialDirtyMechanism = true;
    this.asyncObjectInfo = [];
    this.textureKeys.clear();
    this.textureIndex.clear();

    Profile.end('Total');
    performance.loadTextureTotal = Profile.get('Total') ?? 0;
  }
  private async loadTextureByType(filterTypes: AsyncTextureType[]) {
    const textureInfos = this.asyncObjectInfo.flatMap((info) =>
      info.meshInfos.flatMap((meshInfo) =>
        meshInfo.textureInfos.flatMap((textureInfo) => {
          if (filterTypes.includes(textureInfo.type)) return textureInfo;
          return [];
        })
      )
    );

    const loadTexture = (key: string, info: AsyncTextureInfo) => {
      const { uri, textureInfo, sampler } = info;
      return new Promise<void>((resolve, reject) => {
        const noMipmap =
          sampler.minFilter &&
          (sampler.minFilter === TextureMinFilter.NEAREST.valueOf() ||
            sampler.minFilter === TextureMinFilter.LINEAR.valueOf());

        const samplerData = {
          noMipMaps: noMipmap,
          samplingMode: this.getTextureSamplingMode(sampler),
          wrapU: this.getTextureWrapMode(sampler.wrapS as unknown as TextureWrapMode),
          wrapV: this.getTextureWrapMode(sampler.wrapT as unknown as TextureWrapMode),
        };

        const textureCreationOptions: ITextureCreationOptions = {
          noMipmap: samplerData.noMipMaps,
          invertY: false,
          samplingMode: samplerData.samplingMode,
          onLoad: () => {
            resolve();
          },
          loaderOptions: textureInfo.nonColorData ? { useRGBAIfASTCBC7NotAvailableWhenUASTC: true } : undefined,
          useSRGBBuffer: textureInfo.nonColorData,
          mimeType: 'image/ktx2',
        };
        const texture = new Texture(buildSrcUrl(uri), undefined, textureCreationOptions);
        texture.name = key;
        texture.level = 1;

        texture.wrapU = samplerData.wrapU;
        texture.wrapV = samplerData.wrapV;

        if (textureInfo.texCoord) {
          texture.coordinatesIndex = textureInfo.texCoord;
        }

        const KHR_texture_transform = textureInfo.extensions?.KHR_texture_transform;
        if (KHR_texture_transform) {
          if (KHR_texture_transform.offset) {
            texture.uOffset = KHR_texture_transform.offset[0];
            texture.vOffset = KHR_texture_transform.offset[1];
          }

          texture.uRotationCenter = 0;
          texture.vRotationCenter = 0;

          if (KHR_texture_transform.rotation) {
            texture.wAng = -KHR_texture_transform.rotation;
          }

          if (KHR_texture_transform.scale) {
            texture.uScale = KHR_texture_transform.scale[0];
            texture.vScale = KHR_texture_transform.scale[1];
          }

          if (KHR_texture_transform.texCoord != undefined) {
            texture.coordinatesIndex = KHR_texture_transform.texCoord;
          }
        }

        this.loadedTexture.set(texture.name, texture);
      });
    };

    await Promise.all(
      textureInfos.flatMap((info) => {
        const key = this.getTextureKeyByInfo(info);
        if (!this.loadedTexture.has(key)) {
          return loadTexture(key, info);
        }
      })
    );
  }
  private async applyTextureByType(filterTypes: AsyncTextureType[]) {
    await Promise.all(
      this.asyncObjectInfo.flatMap((info) =>
        info.meshInfos.flatMap((meshInfo) => {
          const mesh = meshInfo.mesh;
          if (!mesh) return;
          const material = meshInfo.material;
          if (!material) return;

          meshInfo.textureInfos.forEach((textureInfo) => {
            const type = textureInfo.type;
            if (filterTypes.includes(type)) {
              this.applyTextureInfoToMaterial(textureInfo, material);
            }
          });

          return material.forceCompilationAsync(mesh);
        })
      )
    );
    this.renderManager.renderUpdate();
  }
  private applyTextureInfoToMaterial(info: AsyncTextureInfo, material: Material) {
    const type = info.type;
    const key = this.getTextureKeyByInfo(info);
    if (this.loadedTexture.has(key)) {
      const texture = this.loadedTexture.get(key)!;
      switch (type) {
        case AsyncTextureType.BaseColor:
          if (material instanceof PBRMaterial) {
            material.albedoTexture = texture;
            if (material.transparencyMode !== 0) {
              texture.hasAlpha = true;
              material.useAlphaFromAlbedoTexture = true;
            }
            if (material.transparencyMode === 1) {
              material.alphaCutOff = 0.5;
            }
          } else if (material instanceof StandardMaterial) {
            material.diffuseTexture = texture;
            if (material.transparencyMode !== 0) {
              texture.hasAlpha = true;
            }
            if (material.transparencyMode === 1) {
              material.alphaCutOff = 0.5;
            }
          }
          break;
        case AsyncTextureType.Normal:
          if (material instanceof PBRMaterial || material instanceof StandardMaterial) {
            material.bumpTexture = texture;

            const scene = EngineStore.LastCreatedScene;
            if (scene) {
              material.invertNormalMapX = !scene.useRightHandedSystem;
              material.invertNormalMapY = scene.useRightHandedSystem;
            }
            //@ts-ignore
            if (info.textureInfo.scale != undefined && material.bumpTexture) {
              //@ts-ignore
              material.bumpTexture.level = info.textureInfo.scale;
            }

            if (material instanceof PBRMaterial) {
              material.disableBumpMap = false;
              material.forceIrradianceInFragment = true;
            }
          }

          break;
        case AsyncTextureType.Occlusion:
          if (material instanceof PBRMaterial) {
            material.ambientTexture = texture;
            material.ambientTextureStrength = info.textureInfo
              ? //@ts-ignore
                info.textureInfo.strength ?? 1
              : 1;
          } else if (material instanceof StandardMaterial) {
            material.ambientTexture = texture;
          }
          break;
        case AsyncTextureType.LightMap:
          if (material instanceof PBRMaterial) {
            material.lightmapTexture = texture;
            material.useLightmapAsShadowmap = true;
          } else if (material instanceof StandardMaterial) {
            material.lightmapTexture = texture;
            material.useLightmapAsShadowmap = true;
          }
          break;
      }
    }
  }
  private async applyReflectionProbe(scene: Scene) {
    await Promise.all(
      this.asyncObjectInfo.flatMap((info) =>
        info.meshInfos.flatMap(async (meshInfo) => {
          if (
            meshInfo.reflectionProbe &&
            meshInfo.mesh &&
            meshInfo.material &&
            meshInfo.material instanceof PBRMaterial
          ) {
            const textureName = `${meshInfo.reflectionProbe.name}.env`;
            let envTex = this.loadedEnvironment.get(textureName);
            if (!envTex) {
              const envTex = this.environmentLoader.loadEnvironment(
                scene,
                this.imagePath ? `${this.imagePath}environment/${textureName}` : `assets/environment/${textureName}`,
                {
                  intensity: meshInfo.reflectionProbe.intensity,
                }
              );
              this.loadedEnvironment.set(textureName, envTex);
              let isReady = false;
              while (!isReady) {
                await new Promise<void>((resolve) => {
                  setTimeout(resolve, 100);
                });
                isReady = envTex.isReady();
              }
            }
          }
        })
      )
    );

    await Promise.all(
      this.asyncObjectInfo.flatMap((info) =>
        info.meshInfos.flatMap(async (meshInfo) => {
          if (
            meshInfo.reflectionProbe &&
            meshInfo.mesh &&
            meshInfo.material &&
            meshInfo.material instanceof PBRMaterial
          ) {
            const textureName = `${meshInfo.reflectionProbe.name}.env`;
            let envTex = this.loadedEnvironment.get(textureName);
            if (envTex) {
              meshInfo.material.reflectionTexture = envTex;
              await meshInfo.material.forceCompilationAsync(meshInfo.mesh);
            }
          }
        })
      )
    );
  }

  //------------------------------------------------------------------------------------
  // #### Texture Utile ####
  //------------------------------------------------------------------------------------
  private getTextureKeyByInfo(info: AsyncTextureInfo) {
    const url = info.uri;
    const texcood = info.textureInfo.texCoord ?? 0;
    const scale = this.getTextureScale(info.textureInfo);
    const uv = this.getTextureUV(info.sampler);

    return `${url}_${texcood}_${scale[0]}_${scale[1]}_${uv[0]}_${uv[1]}`;
  }
  private getTextureScale(textureInfo: ITextureInfo) {
    if (
      textureInfo.extensions &&
      textureInfo.extensions.KHR_texture_transform &&
      textureInfo.extensions.KHR_texture_transform.scale
    ) {
      return textureInfo.extensions.KHR_texture_transform.scale;
    } else {
      return [1, 1];
    }
  }
  private getTextureUV(samplerInfo: ISampler) {
    return samplerInfo.wrapS && samplerInfo.wrapT ? [samplerInfo.wrapS, samplerInfo.wrapT] : [1, 1];
  }
  private getTextureSamplingMode(sampler: ISampler): number {
    const magFilter = sampler.magFilter?.valueOf() ?? TextureMagFilter.LINEAR;
    const minFilter = sampler.minFilter?.valueOf() ?? TextureMinFilter.LINEAR_MIPMAP_LINEAR;

    if (magFilter === TextureMagFilter.LINEAR) {
      switch (minFilter) {
        case TextureMinFilter.NEAREST:
          return Texture.LINEAR_NEAREST;
        case TextureMinFilter.LINEAR:
          return Texture.LINEAR_LINEAR;
        case TextureMinFilter.NEAREST_MIPMAP_NEAREST:
          return Texture.LINEAR_NEAREST_MIPNEAREST;
        case TextureMinFilter.LINEAR_MIPMAP_NEAREST:
          return Texture.LINEAR_LINEAR_MIPNEAREST;
        case TextureMinFilter.NEAREST_MIPMAP_LINEAR:
          return Texture.LINEAR_NEAREST_MIPLINEAR;
        case TextureMinFilter.LINEAR_MIPMAP_LINEAR:
          return Texture.LINEAR_LINEAR_MIPLINEAR;
        default:
          return Texture.LINEAR_LINEAR_MIPLINEAR;
      }
    } else {
      if (magFilter !== TextureMagFilter.NEAREST) {
      }

      switch (minFilter) {
        case TextureMinFilter.NEAREST:
          return Texture.NEAREST_NEAREST;
        case TextureMinFilter.LINEAR:
          return Texture.NEAREST_LINEAR;
        case TextureMinFilter.NEAREST_MIPMAP_NEAREST:
          return Texture.NEAREST_NEAREST_MIPNEAREST;
        case TextureMinFilter.LINEAR_MIPMAP_NEAREST:
          return Texture.NEAREST_LINEAR_MIPNEAREST;
        case TextureMinFilter.NEAREST_MIPMAP_LINEAR:
          return Texture.NEAREST_NEAREST_MIPLINEAR;
        case TextureMinFilter.LINEAR_MIPMAP_LINEAR:
          return Texture.NEAREST_LINEAR_MIPLINEAR;
        default:
          return Texture.NEAREST_NEAREST_MIPNEAREST;
      }
    }
  }
  private getTextureWrapMode(mode: TextureWrapMode | undefined): number {
    mode = mode == undefined ? TextureWrapMode.REPEAT : mode;

    switch (mode) {
      case TextureWrapMode.CLAMP_TO_EDGE:
        return Texture.CLAMP_ADDRESSMODE;
      case TextureWrapMode.MIRRORED_REPEAT:
        return Texture.MIRROR_ADDRESSMODE;
      case TextureWrapMode.REPEAT:
        return Texture.WRAP_ADDRESSMODE;
      default:
        return Texture.WRAP_ADDRESSMODE;
    }
  }
}
